import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import ContentfulExternalLinkButton from '@entries/contentful-external-link-button'
import ContentfulFootnoteSup from '@entries/contentful-footnote-sup'
import contentfulInternalLinkButtonButton from '@entries/contentful-internal-link-button-button'
import ContentfulPDFLinkButton from '@entries/contentful-pdf-link-button'
import contentfulSubscriptionLinkButton from '@entries/contentful-subscription-link-button'
import { withDisplayName } from '@hocs/with-display-name'
import { children } from '@render-node/utils/children'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { graphql } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React from 'react'

import { ContentType } from './content-types'

const ContentfulShortContentBlock: Utils.EntryFC<
  Queries.ContentfulShortContentBlockFragment
> = ({
  content: { header, description, cta, illustration },
  entryInspectorProps,
  fieldInspectorProps,
}) => (
  <div
    className="mx-auto flex max-w-4xl flex-col rounded-[20px] border border-solid border-grey-100 bg-[#F7F7F7] drop-shadow group-[.is-grey]/section-template:bg-background-default tablet:h-[280px] tablet:flex-row-reverse tablet:gap-20"
    {...entryInspectorProps()}
  >
    <div className="aspect-square">
      <GatsbyImage
        {...fieldInspectorProps({ fieldId: 'illustration' })}
        alt={illustration.description}
        className="h-full w-full"
        image={getImage(illustration)}
      />
    </div>
    <div className="flex flex-col justify-between p-6 pt-8 tablet:p-12">
      <div className="flex flex-col text-left">
        <RichText
          {...fieldInspectorProps({ fieldId: 'header' })}
          className="mb-2 text-[20px] font-medium *:text-left"
          content={header}
          options={{
            renderNode: {
              [BLOCKS.PARAGRAPH]: children,
              [INLINES.EMBEDDED_ENTRY]: {
                [ContentType.ContentfulFootnote]: ContentfulFootnoteSup,
              },
            },
          }}
        />
        <RichText
          {...fieldInspectorProps({ fieldId: 'description' })}
          className=""
          content={description}
          options={{
            renderNode: {
              [BLOCKS.PARAGRAPH]: children,
              [INLINES.EMBEDDED_ENTRY]: {
                [ContentType.ContentfulFootnote]: ContentfulFootnoteSup,
              },
            },
          }}
        />
      </div>
      <Entry
        {...fieldInspectorProps({ fieldId: 'cta' })}
        content={cta}
        mapping={{
          [ContentType.ContentfulExternalLink]: ContentfulExternalLinkButton,
          [ContentType.ContentfulInternalLink]:
            contentfulInternalLinkButtonButton,
          [ContentType.ContentfulSubscriptionLink]:
            contentfulSubscriptionLinkButton,
          [ContentType.ContentfulPdfLink]: ContentfulPDFLinkButton,
        }}
      />
    </div>
  </div>
)

export const query = graphql`
  fragment ContentfulShortContentBlockAsset on ContentfulAsset {
    ...Asset
    gatsbyImageData(
      placeholder: BLURRED
      layout: FULL_WIDTH
      formats: [AVIF, WEBP, AUTO]
    )
  }

  fragment ContentfulShortContentBlock on ContentfulShortContentBlock {
    ...ContentfulEntry
    header {
      rich
      references {
        ...ContentfulFootnoteSup
      }
    }
    description {
      rich
      references {
        ...ContentfulFootnoteSup
      }
    }
    cta {
      ...ContentfulExternalLinkButton
      ...ContentfulInternalLinkButtonButton
      ...ContentfulSubscriptionLinkButton
      ...ContentfulPDFLinkButton
    }
    illustration {
      ...ContentfulShortContentBlockAsset
    }
  }
`

export default withDisplayName(
  ContentfulShortContentBlock,
  'ContentfulShortContentBlock'
)
